import { Component, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { useAuthSession } from '../contexts/AuthContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';
import { api } from '../services/api.js';

/**
 * Login — Propy-inspired dark sign-in page.
 *
 * Two-column, full-height layout:
 *   - Left column (vertically centered): brand wordmark.
 *   - Right column (full height, space-between): sign-in form at the
 *     top, social sign-in + sign-up link at the bottom — the generous
 *     gap between the two groups is the whole visual point.
 *
 * Mobile (<730px): single column, wordmark hidden, stacked top-to-bottom.
 *
 * Real auth against POST /api/re/auth/login. Forgot Password / Google /
 * Apple / Contact Us all show a toast — those flows aren't wired up yet.
 */

const INPUT_CLS =
  'w-full h-[42px] px-3 rounded-[4px] bg-[#1A1A1A] text-white text-[14px] ' +
  'placeholder:text-[#6B6E7E] border-0 focus:outline-none ' +
  'focus:ring-2 focus:ring-[#FF8000]/60 transition-shadow';

const BG_STYLE = {
  backgroundColor: '#000000',
  backgroundImage: [
    'radial-gradient(circle at 90% 20%, rgba(255, 100, 0, 0.35) 0%, transparent 30%)',
    'radial-gradient(circle at 10% 80%, rgba(255, 100, 0, 0.35) 0%, transparent 30%)',
  ].join(', '),
};

export default function Login() {
  return (
    <LoginErrorBoundary>
      <LoginInner />
    </LoginErrorBoundary>
  );
}

function LoginInner() {
  // Every hook used by this component must sit above the first conditional
  // return — React tracks hooks by call order, so changing the number of
  // calls between renders throws "Rendered fewer hooks than expected"
  // (React error #300/#310). That was the source of the blank Login page:
  // ``useState(googleRedirecting)`` was declared *after* the
  // ``if (isAuthenticated) return <Navigate … />`` short-circuit.
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const auth = useAuthSession() || {};
  const login = auth.login;
  const isAuthenticated = Boolean(auth.isAuthenticated);
  const sessionUser = auth.user || null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [googleRedirecting, setGoogleRedirecting] = useState(false);

  // Where to land after auth. Honors the ProtectedRoute "from" hint if the
  // user was redirected here from a gated page; otherwise dispatches by
  // role so super admins skip the customer-facing briefing.
  const fromPath = location?.state?.from?.pathname || null;

  const landingFor = (role) => {
    if (fromPath) return fromPath;
    if (role === 'super_admin') return '/admin/clients';
    return '/dashboard';
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(landingFor(sessionUser?.role), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionUser?.role]);

  // Every hook has now been called — it's safe to bail out on an
  // authenticated session.
  if (isAuthenticated) {
    return <Navigate to={landingFor(sessionUser?.role)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      if (typeof login !== 'function') {
        throw new Error('Auth service is not ready.');
      }
      const user = await login({ email: email.trim(), password });
      showToast('Signed in', { type: 'success' });
      navigate(landingFor(user?.role), { replace: true });
    } catch (err) {
      const msg = diagnoseLoginError(err);
      setError(msg);
      showToast(msg, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const comingSoon = (label) => () => showToast(`${label} coming soon`, { type: 'info' });

  const onGoogleSignIn = async () => {
    if (googleRedirecting) return;
    setGoogleRedirecting(true);
    try {
      const { data } = await api.get('/auth/google/redirect');
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      showToast('Google sign-in is not available right now.', { type: 'error' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : err?.message || 'Could not start Google sign-in.';
      showToast(msg, { type: 'error' });
    } finally {
      setGoogleRedirecting(false);
    }
  };

  return (
    <div
      style={{
        ...BG_STYLE,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        color: 'white',
      }}
    >
      {/* Two-column container — dead center of the viewport. Columns are
          top-aligned so the wordmark sits on the same baseline as the
          Sign-in heading (Propy reference). */}
      <div className="flex flex-col w-full px-5 gap-10 md:flex-row md:items-start md:max-w-[1000px] md:px-[48px] md:gap-[80px]">
        {/* Left — branding (hidden on mobile, visible on md+) */}
        <div className="hidden md:block md:flex-1">
          <div
            style={{
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '0.1em',
              color: 'white',
              lineHeight: 1,
            }}
          >
            ATLACIS SOFTWARE
          </div>
          <div
            style={{
              fontSize: '13px',
              color: '#94a3b8',
              marginTop: '6px',
              lineHeight: 1,
            }}
          >
            Command Center
          </div>
        </div>

        {/* Right — form */}
        <div className="w-full md:flex-1 md:max-w-[400px]">
          <div>
            {/* Brand wordmark — shown only on mobile since left column handles desktop */}
            <div className="mb-6 md:hidden">
              <div
                style={{
                  fontWeight: 700,
                  fontSize: '18px',
                  letterSpacing: '0.1em',
                  color: 'white',
                  lineHeight: 1,
                }}
              >
                ATLACIS SOFTWARE
              </div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '6px', lineHeight: 1 }}>
                Command Center
              </div>
            </div>

            <h1
              className="text-[26px] leading-none text-white"
              style={{ fontWeight: 500 }}
            >
              Sign in
            </h1>

            <form onSubmit={onSubmit} className="mt-7 space-y-4" noValidate>
              <div>
                <label
                  htmlFor="email"
                  className="block text-[13px] text-[#AAACBA] mb-1.5"
                  style={{ fontWeight: 700 }}
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLS}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-[13px] text-[#AAACBA] mb-1.5"
                  style={{ fontWeight: 700 }}
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${INPUT_CLS} pr-10`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 w-10 flex items-center justify-center
                               text-[#AAACBA] hover:text-white transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="inline-flex items-center gap-2 text-[13px] text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-[18px] w-[18px] rounded-[3px] border-2 border-white bg-transparent
                               accent-white cursor-pointer"
                  />
                  Remember Me
                </label>
                <button
                  type="button"
                  onClick={comingSoon('Password reset')}
                  className="text-[13px] transition-colors hover:underline"
                  style={{ color: '#FDBA74', fontWeight: 500 }}
                >
                  Forgot your password?
                </button>
              </div>

              {error ? (
                <div className="text-[13px] text-[#F87171]" role="alert">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="w-full min-h-[48px] md:min-h-0 md:h-[40px] rounded-[4px] bg-white text-[14px] font-semibold
                           transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ color: '#1A1A1A' }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = '#F9F9FA';
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = '#FFFFFF';
                }}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <div className="mt-4 flex items-center gap-3" aria-hidden="true">
              <div className="flex-1 h-px bg-[#2A2A2A]" />
              <span className="text-[12px] text-[#6B6E7E]">or</span>
              <div className="flex-1 h-px bg-[#2A2A2A]" />
            </div>

            <div className="mt-4">
              <SocialButton
                label={googleRedirecting ? 'Redirecting…' : 'Continue with Google'}
                icon={<GoogleGlyph />}
                onClick={onGoogleSignIn}
                disabled={googleRedirecting}
              />
            </div>

            <p className="mt-5 text-[13px] text-[#AAACBA]">
              Don&apos;t have an account?{' '}
              <a
                href="https://atlacis.com/book-call"
                target="_blank"
                rel="noreferrer"
                style={{ color: '#FDBA74', fontWeight: 600 }}
                className="hover:underline"
              >
                Contact us
              </a>
            </p>

            <div className="mt-3 flex items-center gap-5">
              <a
                href="https://atlacis.com"
                target="_blank"
                rel="noreferrer"
                className="text-[13px] text-[#94a3b8] hover:text-white hover:underline transition-colors"
              >
                ← Back to atlacis.com
              </a>
              <a
                href="https://atlacis.com"
                target="_blank"
                rel="noreferrer"
                className="text-[13px] text-[#94a3b8] hover:text-white hover:underline transition-colors"
              >
                Run an Audit
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer — absolute, never moves */}
      <div
        style={{
          position: 'absolute',
          bottom: '24px',
          left: 0,
          right: 0,
          fontSize: '12px',
          color: '#94a3b8',
        }}
        className="flex flex-col items-center gap-1 px-5 text-center md:flex-row md:justify-between md:px-[40px] md:text-left md:gap-0"
      >
        <span>© 2026 Atlacis Software Technologies LLC</span>
        <span>Privacy Policy · Terms of Service</span>
      </div>
    </div>
  );
}

// Turn a login failure into something actionable. The old fallback
// collapsed every error into "Invalid credentials", which hides network
// failures and missing-backend 404s behind the same text.
function diagnoseLoginError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;

  const status = err?.response?.status;
  if (status === 404) return 'Login service not reachable (404). Backend may not be deployed.';
  if (status === 502 || status === 503 || status === 504) {
    return 'Login service is unavailable. Please try again in a moment.';
  }
  if (status >= 500) return `Login service error (${status}). Try again shortly.`;
  if (!err?.response) return 'Network error — could not reach the login service.';
  return 'Invalid credentials';
}

// ── Social buttons — Propy-style: white, rounded, icon on left ───────────────

function SocialButton({ label, icon, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="relative w-full min-h-[48px] md:min-h-0 md:h-[40px] rounded-[4px] bg-white text-[#1A1A1A]
                 text-[14px] font-medium flex items-center justify-center
                 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = '#F9F9FA';
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = '#FFFFFF';
      }}
    >
      {/* Fixed 20x20 icon box so Google's square mark and Apple's
          narrower glyph both sit at the same optical left edge. */}
      <span className="absolute left-4 h-5 w-5 flex items-center justify-center">
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

// Error boundary wrapping the whole Login page. A render-time exception
// inside LoginInner (bad hook order, a thrown render, a missing provider)
// would otherwise unmount the tree and leave a blank white screen — we'd
// rather show a friendly recovery card so the user can retry or contact
// support.
class LoginErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || 'Something went wrong loading the login page.',
    };
  }

  componentDidCatch(error, info) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('Login render error', error, info);
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center px-6 text-white"
        style={BG_STYLE}
      >
        <div
          className="w-full max-w-[420px] rounded-[8px] p-6"
          style={{ backgroundColor: '#0a0f1a', border: '1px solid #1e293b' }}
        >
          <h2 className="text-[18px] font-semibold mb-2">
            We couldn&apos;t load the login page
          </h2>
          <p className="text-[13px] text-[#AAACBA] mb-4">
            {this.state.message}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="w-full h-[42px] rounded-[4px] bg-white text-[#1A1A1A] text-[14px] font-semibold"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

